import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_PREFIX, apiFetch, apiGet, type Envelope, type Page } from '@/api/client'

/**
 * One stop on a rep's day. Carries enough of its customer to draw a pin and a list row,
 * because the alternative on a phone is twenty round trips to open one screen.
 */
export interface RouteStop {
  visit_id: string
  visit_number: string
  stop_sequence: number
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled'
  planned_at: string | null
  checked_in_at: string | null
  outcome: string | null
  customer_id: string
  customer_name: string
  customer_address: string | null
  customer_phone: string | null
  latitude: number | null
  longitude: number | null
  leg_distance_m: number | null
  leg_duration_s: number | null
}

export interface RouteRead {
  id: string
  route_number: string
  user_id: string
  route_date: string
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled'
  start_lat: number | null
  start_lng: number | null
  optimized_at: string | null
  total_distance_m: number | null
  total_duration_s: number | null
  /**
   * `google` when the Routes API answered, `haversine` when the distances are
   * straight-line estimates. The screen says which — presenting a crow-flies figure as a
   * driving time is the kind of small lie a rep plans their afternoon around.
   */
  distance_provider: 'google' | 'haversine' | null
  stops: RouteStop[]
  created_at: string
  updated_at: string
}

/**
 * The signed-in rep's round for a day. `null` — not an error — when nothing is planned,
 * which is the ordinary state of a Sunday.
 */
export function useTodaysRoute(on?: string) {
  return useQuery({
    queryKey: ['field_routes', 'today', on ?? 'today'],
    queryFn: async () => {
      const qs = on ? `?on=${encodeURIComponent(on)}` : ''
      return apiGet<RouteRead | null>(`${API_PREFIX}/field-routes/today${qs}`)
    },
  })
}

export function useRoute(routeId: string | undefined) {
  return useQuery({
    queryKey: ['field_routes', routeId],
    queryFn: async () => apiGet<RouteRead>(`${API_PREFIX}/field-routes/${routeId}`),
    enabled: Boolean(routeId),
  })
}

/** Planned rounds the caller may see — their own, their team's, or everyone's. */
export function useRoutes(filters: { date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: ['field_routes', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams({ page_size: '100' })
      if (filters.date_from) params.set('date_from', filters.date_from)
      if (filters.date_to) params.set('date_to', filters.date_to)
      return apiFetch<Page<RouteRead>>(`${API_PREFIX}/field-routes?${params.toString()}`)
    },
    retry: false,
  })
}

export interface RouteRep {
  id: string
  full_name: string
  email: string
}

/** Field reps the caller may plan for — everyone, their team, or just themselves. */
export function useRouteReps() {
  return useQuery({
    queryKey: ['field_routes', 'reps'],
    queryFn: async () => apiGet<RouteRep[]>(`${API_PREFIX}/field-routes/reps`),
    retry: false,
    staleTime: 5 * 60_000,
  })
}

export interface RouteCreate {
  route_date: string
  customer_ids: string[]
  start_lat?: number
  start_lng?: number
  optimize?: boolean
  /** The rep whose day this is; omitted, the caller's own. */
  user_id?: string
  /** Local clock times ("14:30") promised to some shops, by customer id. */
  visit_times?: Record<string, string>
}

export function useCreateRoute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: RouteCreate) => {
      const response = await apiFetch<Envelope<RouteRead>>(`${API_PREFIX}/field-routes`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['field_routes'] })
    },
  })
}

/**
 * Re-order what is left of the round, from where the rep is standing now. The whole point
 * of passing a position: three stops in and running late, the useful question is the best
 * order for the rest from *here*, not the one that was best from the depot at eight.
 */
export function useOptimizeRoute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      routeId,
      start,
    }: {
      routeId: string
      start?: { lat: number; lng: number }
    }) => {
      const response = await apiFetch<Envelope<RouteRead>>(
        `${API_PREFIX}/field-routes/${routeId}/optimize`,
        {
          method: 'POST',
          body: JSON.stringify(start ? { start_lat: start.lat, start_lng: start.lng } : {}),
        },
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['field_routes', data.id], data)
      void queryClient.invalidateQueries({ queryKey: ['field_routes', 'today'] })
    },
  })
}

/** Metres as a rep reads them: "800 m", "2.4 km". */
export function formatDistance(metres: number | null | undefined): string {
  if (metres == null) return '—'
  if (metres < 1000) return `${Math.round(metres)} m`
  return `${(metres / 1000).toFixed(1)} km`
}

/** Seconds as a rep reads them: "12 min", "1 h 20 min". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`
}
